/**
 * WeeklyScheduleEditor Component
 * Main editor for weekly schedule type - shows all 7 days with their steps
 */
import React, { useState } from 'react';
import {
  Box,
  Alert,
  Snackbar,
  Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Schedule,
  DayOfWeek,
  WeekReference,
  ScheduleStep,
  ALL_DAYS_OF_WEEK,
  DEFAULT_WEEK_REFERENCE,
} from '../../types/schedule';
import { Action } from '../../types/action';
import { scheduleService } from '../../services/scheduleService';
import { WeekSelector } from './WeekSelector';
import { WeeklyDayCard } from './WeeklyDayCard';
import { CopyDayDialog } from './CopyDayDialog';
import { AddStepDialog } from './AddStepDialog';
import { StepEditor } from './StepEditor';

interface WeeklyScheduleEditorProps {
  schedule: Schedule;
  deviceId: string;
  deviceActions: Action[];
  deviceSchedules: Schedule[];
  onScheduleUpdated: () => void;
  onActionsChange?: () => void;
  onSchedulesChange?: () => void;
}

export const WeeklyScheduleEditor: React.FC<WeeklyScheduleEditorProps> = ({
  schedule,
  deviceId,
  deviceActions,
  deviceSchedules,
  onScheduleUpdated,
  onActionsChange,
  onSchedulesChange,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dialog states
  const [copyDaySource, setCopyDaySource] = useState<DayOfWeek | null>(null);
  const [addStepDay, setAddStepDay] = useState<DayOfWeek | null>(null);
  const [editingStep, setEditingStep] = useState<{ day: DayOfWeek; step: ScheduleStep } | null>(null);

  const weekReference = schedule.weekReference || DEFAULT_WEEK_REFERENCE;

  const handleWeekReferenceChange = async (newRef: WeekReference) => {
    setLoading(true);
    try {
      await scheduleService.updateSchedule(deviceId, schedule.id, {
        weekReference: newRef,
      });
      onScheduleUpdated();
    } catch (err) {
      setError(t('schedules.errors.failedToUpdateSchedule'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDayEnabled = async (day: DayOfWeek, enabled: boolean) => {
    setLoading(true);
    try {
      await scheduleService.updateDayConfig(deviceId, schedule.id, day, {
        isEnabled: enabled,
      });
      setSuccess(enabled ? t('schedules.weekly.dayEnabled') : t('schedules.weekly.dayDisabled'));
      onScheduleUpdated();
    } catch (err) {
      setError(t('schedules.errors.failedToUpdateSchedule'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddStep = (day: DayOfWeek) => {
    setAddStepDay(day);
  };

  const handleStepAdded = () => {
    setAddStepDay(null);
    onScheduleUpdated();
  };

  const handleEditStep = (day: DayOfWeek, step: ScheduleStep) => {
    setEditingStep({ day, step });
  };

  const handleStepUpdated = () => {
    setEditingStep(null);
    onScheduleUpdated();
  };

  const handleDeleteStep = async (day: DayOfWeek, stepId: string) => {
    setLoading(true);
    try {
      await scheduleService.deleteDayStep(deviceId, schedule.id, day, stepId);
      setSuccess(t('schedules.success.stepDeleted'));
      onScheduleUpdated();
    } catch (err) {
      setError(t('schedules.errors.failedToDeleteStep'));
    } finally {
      setLoading(false);
    }
  };

  const handleReorderSteps = async (day: DayOfWeek, stepIds: string[]) => {
    try {
      await scheduleService.reorderDaySteps(deviceId, schedule.id, day, stepIds);
      onScheduleUpdated();
    } catch (err) {
      setError(t('schedules.errors.failedToReorderSteps'));
    }
  };

  const handleCopyDay = (sourceDay: DayOfWeek) => {
    setCopyDaySource(sourceDay);
  };

  const handleCopyDayConfirm = async (targetDays: DayOfWeek[], clearExisting: boolean) => {
    if (!copyDaySource || targetDays.length === 0) return;

    try {
      // Copy to all selected days
      for (const targetDay of targetDays) {
        await scheduleService.copyDaySteps(
          deviceId,
          schedule.id,
          copyDaySource,
          targetDay,
          { clearExisting }
        );
      }
      setSuccess(t('schedules.weekly.stepsCopied'));
      onScheduleUpdated();
    } catch (err) {
      setError(t('schedules.weekly.failedToCopySteps'));
      throw err;
    }
  };

  const getDayConfig = (day: DayOfWeek) => {
    return schedule.days?.[day] || { isEnabled: true, steps: null };
  };

  return (
    <Box>
      {/* Week Selector */}
      <Box sx={{ mb: 3 }}>
        <WeekSelector
          value={weekReference}
          onChange={handleWeekReferenceChange}
          disabled={loading}
        />
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Days List */}
      <Box>
        {ALL_DAYS_OF_WEEK.map((day) => (
          <WeeklyDayCard
            key={day}
            day={day}
            config={getDayConfig(day)}
            deviceActions={deviceActions}
            deviceSchedules={deviceSchedules}
            onToggleEnabled={(enabled) => handleToggleDayEnabled(day, enabled)}
            onAddStep={() => handleAddStep(day)}
            onEditStep={(step) => handleEditStep(day, step)}
            onDeleteStep={(stepId) => handleDeleteStep(day, stepId)}
            onReorderSteps={(stepIds) => handleReorderSteps(day, stepIds)}
            onCopyDay={() => handleCopyDay(day)}
            disabled={loading}
          />
        ))}
      </Box>

      {/* Copy Day Dialog */}
      {copyDaySource && (
        <CopyDayDialog
          open={true}
          sourceDay={copyDaySource}
          onClose={() => setCopyDaySource(null)}
          onCopy={handleCopyDayConfirm}
        />
      )}

      {/* Add Step Dialog */}
      {addStepDay && (
        <AddStepDialog
          open={true}
          scheduleId={schedule.id}
          deviceId={deviceId}
          currentScheduleId={schedule.id}
          deviceActions={deviceActions}
          deviceSchedules={deviceSchedules}
          day={addStepDay}
          onClose={() => setAddStepDay(null)}
          onAdded={handleStepAdded}
          onActionsChange={onActionsChange}
          onSchedulesChange={onSchedulesChange}
        />
      )}

      {/* Edit Step Dialog */}
      {editingStep && (
        <StepEditor
          step={editingStep.step}
          open={true}
          onClose={() => setEditingStep(null)}
          onSaved={handleStepUpdated}
          scheduleId={schedule.id}
          deviceId={deviceId}
          deviceActions={deviceActions}
          deviceSchedules={deviceSchedules}
          day={editingStep.day}
        />
      )}

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WeeklyScheduleEditor;
